import { memo, useState, useEffect } from 'preact/compat';
import { useAppDispatch, useAppSelector } from '../hooks';
import { fetchOfferBuyers } from '../store/offersSlice';
import { selectSelectedOffer } from '../store/selectors';
import LoadingOverlay from './LoadingOverlay';
import SelectionDetailPage from './SelectionDetailPage';
import VendorBackgroundTooltip from './VendorBackgroundTooltip';
import OfferSkeleton from './OfferSkeleton';
import { apiFetch } from '../api/client';

interface BuyerMappingItem {
    id: number;
    oracle_buyer_code: number;
    oracle_buyer_name: string;
    b24_user_id: number | null;
    b24_user_name: string | null;
    b24_manager_id: number | null;
    role: string;
}

const SelectionPage = memo(() => {
    const dispatch = useAppDispatch();
    const offer = useAppSelector(selectSelectedOffer);
    const buyers = useAppSelector(s => s.offers.buyers);
    const loading = useAppSelector(s => s.offers.loading);
    const refs = useAppSelector(s => s.references.data);
    const selectedCode = offer?.code ?? null;

    const [selectedBuyers, setSelectedBuyers] = useState<Set<number>>(new Set());
    const [selectedBuyer, setSelectedBuyer] = useState<{ buyerCodes: number[]; buyerName: string } | null>(null);
    const [filterStatus, setFilterStatus] = useState('all');
    const [filterTG, setFilterTG] = useState('all');
    const [searchText, setSearchText] = useState('');
    const [showMy, setShowMy] = useState(false);
    const [buyerMapping, setBuyerMapping] = useState<BuyerMappingItem[]>([]);
    const [hoveredRow, setHoveredRow] = useState<number | null>(null);

    const wareCategoryBuyers = refs?.ware_category_buyers ?? [];

    // Current user from cookie
    const currentB24UserId = parseInt(document.cookie.match(/b24_user_id=(\d+)/)?.[1] || '0', 10);
    const isAdmin = /(?:^|;\s*)b24_is_admin=1(?:;|$)/.test(document.cookie);

    useEffect(() => {
        if (selectedCode) {
            dispatch(fetchOfferBuyers({ code: selectedCode }));
        }
    }, [dispatch, selectedCode]);

    // Load buyer mapping
    useEffect(() => {
        apiFetch<BuyerMappingItem[]>('/buyer-mapping').then(setBuyerMapping).catch(() => {});
    }, []);

    if (!offer) return <OfferSkeleton />;

    // If buyers selected, show detail view
    if (selectedBuyer) {
        return (
            <SelectionDetailPage
                offerCode={selectedCode!}
                buyerCodes={selectedBuyer.buyerCodes}
                buyerName={selectedBuyer.buyerName}
                onBack={() => {
                    setSelectedBuyer(null);
                    setSelectedBuyers(new Set());
                    if (selectedCode) dispatch(fetchOfferBuyers({ code: selectedCode }));
                }}
            />
        );
    }

    // Filter buyers
    let displayBuyers = [...buyers];

    if (filterStatus !== 'all') {
        displayBuyers = displayBuyers.filter(b => String(b.status_code) === filterStatus);
    }
    if (filterTG !== 'all') {
        displayBuyers = displayBuyers.filter(b => b.ware_category_name === filterTG);
    }
    if (showMy) {
        const myBuyer = localStorage.getItem('user_buyer_code');
        if (myBuyer) displayBuyers = displayBuyers.filter(b => String(b.buyer_code) === myBuyer);
    }
    if (searchText) {
        const s = searchText.toLowerCase();
        displayBuyers = displayBuyers.filter(b =>
            (b.buyer_name || '').toLowerCase().includes(s) ||
            (b.ware_category_name || '').toLowerCase().includes(s)
        );
    }

    displayBuyers.sort((a, b) => (a.ware_category_name || '').localeCompare(b.ware_category_name || '', 'ru'));

    const uniqueStatuses = Array.from(new Set(buyers.map(b => b.status_name).filter(Boolean))) as string[];
    const uniqueTGs = Array.from(new Set(buyers.map(b => b.ware_category_name).filter(Boolean))).sort() as string[];

    // Check if current user can access a buyer-TG row
    const canAccess = (wareGroupCode: number | null): boolean => {
        if (!wareGroupCode) return false;

        // Администратор Б24 имеет полный доступ к отборке (он же ответственный/создатель)
        if (isAdmin) return true;

        if (!currentB24UserId) return false;

        // Find current user's Oracle buyer codes
        const myMappings = buyerMapping.filter(m => m.b24_user_id === currentB24UserId);
        const myOracleCodes = new Set(myMappings.map(m => m.oracle_buyer_code));

        // 1. Am I assigned to this TG?
        const assignedToTG = wareCategoryBuyers.some((wcb: any) =>
            Number(wcb.ware_group_code) === Number(wareGroupCode) && myOracleCodes.has(Number(wcb.buyer_code))
        );
        if (assignedToTG) return true;

        // 2. Am I a manager of someone assigned to this TG?
        const assignedBuyerCodes = wareCategoryBuyers
            .filter((wcb: any) => Number(wcb.ware_group_code) === Number(wareGroupCode))
            .map((wcb: any) => Number(wcb.buyer_code));
        const managedBuyers = buyerMapping.filter(m =>
            m.b24_manager_id === currentB24UserId && assignedBuyerCodes.includes(m.oracle_buyer_code)
        );
        if (managedBuyers.length > 0) return true;

        // 3. Am I the offer responsible?
        if (offer?.buyer_code && myOracleCodes.has(Number(offer.buyer_code))) return true;

        return false;
    };

    const toggleCheckbox = (code: number) => {
        setSelectedBuyers(prev => {
            const next = new Set(prev);
            if (next.has(code)) next.delete(code); else next.add(code);
            return next;
        });
    };

    const handleStartSelection = () => {
        const codes = Array.from(selectedBuyers);
        if (codes.length === 0) return;
        const names = displayBuyers.filter(b => codes.includes(b.code))
            .map(b => b.ware_category_name || b.buyer_name).join(', ');
        setSelectedBuyer({ buyerCodes: codes, buyerName: names });
    };

    // Build tooltip: who can edit this TG
    const getAccessTooltip = (wareGroupCode: number | null): string => {
        if (!wareGroupCode) return '';

        const lines: string[] = [];

        // Buyers assigned to this TG in Oracle
        const assigned = wareCategoryBuyers.filter((wcb: any) => Number(wcb.ware_group_code) === Number(wareGroupCode));
        for (const a of assigned) {
            const mapping = buyerMapping.find(m => m.oracle_buyer_code === Number(a.buyer_code));
            const role = mapping?.role === 'manager' ? 'Руководитель' : Number(a.is_main) === 1 ? 'Главный байер' : 'Байер';
            lines.push(`${a.buyer_name} — ${role}`);

            // Manager of this buyer
            if (mapping?.b24_manager_id) {
                const manager = buyerMapping.find(m => m.b24_user_id === mapping.b24_manager_id);
                if (manager) {
                    lines.push(`${manager.oracle_buyer_name} — Руководитель`);
                }
            }
        }

        // Offer responsible buyer
        if (offer.buyer_code) {
            const responsible = buyerMapping.find(m => m.oracle_buyer_code === Number(offer.buyer_code));
            if (responsible) {
                const already = lines.some(l => l.startsWith(responsible.oracle_buyer_name));
                if (!already) lines.push(`${responsible.oracle_buyer_name} — Ответственный`);
            }
        }

        return [...new Set(lines)].join('\n');
    };

    return (
        <div className="h-full flex flex-col relative">
            <LoadingOverlay visible={loading} />

            {/* Header */}
            <div className="px-6 py-3 border-b border-base-300 bg-base-100 flex items-center gap-3 flex-wrap">
                <input type="text" placeholder="Поиск по байеру, ТГ..." className="input input-bordered input-sm w-48"
                    value={searchText} onInput={e => setSearchText((e.target as HTMLInputElement).value)} />

                <select className="select select-bordered select-sm" value={filterStatus}
                    onChange={e => setFilterStatus((e.target as HTMLSelectElement).value)}>
                    <option value="all">Все статусы</option>
                    {uniqueStatuses.map(s => <option key={s} value={s}>{s}</option>)}
                </select>

                <select className="select select-bordered select-sm" value={filterTG}
                    onChange={e => setFilterTG((e.target as HTMLSelectElement).value)}>
                    <option value="all">Все ТГ</option>
                    {uniqueTGs.map(tg => <option key={tg} value={tg}>{tg}</option>)}
                </select>

                <label className="cursor-pointer">
                    <input type="checkbox" className="hidden" checked={showMy} onChange={e => setShowMy((e.target as HTMLInputElement).checked)} />
                    <span className={`btn btn-sm ${showMy ? 'btn-accent' : 'btn-ghost border border-base-300'}`}>Моя отборка</span>
                </label>

                <div className="flex-1" />

                {selectedBuyers.size > 0 && (
                    <button className="btn btn-sm btn-accent" onClick={handleStartSelection}>
                        Начать отборку ({selectedBuyers.size})
                    </button>
                )}

                {offer.vendor_code && <VendorBackgroundTooltip vendorCode={offer.vendor_code} vendorName={offer.vendor_name || 'Поставщик'} />}
            </div>

            {/* Table */}
            <div className="flex-1 overflow-auto p-6">
                {displayBuyers.length === 0 ? (
                    <div className="text-center py-16 text-base-content/60">Нет данных для отборки</div>
                ) : (
                    <table className="table table-sm w-full">
                        <thead>
                            <tr className="bg-base-200">
                                <th className="w-8">
                                    <input type="checkbox" className="w-4 h-4 rounded accent-info cursor-pointer"
                                        checked={selectedBuyers.size > 0 && selectedBuyers.size === displayBuyers.filter(b => canAccess(b.ware_group_code)).length}
                                        onChange={e => {
                                            if ((e.target as HTMLInputElement).checked) {
                                                setSelectedBuyers(new Set(displayBuyers.filter(b => canAccess(b.ware_group_code)).map(b => b.code)));
                                            } else {
                                                setSelectedBuyers(new Set());
                                            }
                                        }} />
                                </th>
                                <th className="text-xs">ТГ</th>
                                <th className="text-xs">Байер</th>
                                <th className="text-xs">Статус</th>
                                <th className="text-xs">Дата завершения</th>
                                <th className="text-xs text-center">Действие</th>
                            </tr>
                        </thead>
                        <tbody>
                            {displayBuyers.map(b => {
                                const tooltip = getAccessTooltip(b.ware_group_code);
                                const isChecked = selectedBuyers.has(b.code);
                                const hasAccess = canAccess(b.ware_group_code);
                                return (
                                    <tr key={b.code} className={`hover relative ${!hasAccess ? 'opacity-50' : ''}`}
                                        onMouseEnter={() => setHoveredRow(b.code)}
                                        onMouseLeave={() => setHoveredRow(null)}>
                                        <td>
                                            <input type="checkbox" className="w-4 h-4 rounded accent-info cursor-pointer"
                                                checked={isChecked} disabled={!hasAccess}
                                                onChange={() => toggleCheckbox(b.code)} />
                                        </td>
                                        <td className="text-sm">{b.ware_category_name || '\u2014'}</td>
                                        <td className="text-sm">{b.buyer_name || '\u2014'}</td>
                                        <td><span className={`badge badge-sm ${b.status_name === 'Завершено' ? 'badge-success' : 'badge-ghost'}`}>{b.status_name || '\u2014'}</span></td>
                                        <td className="text-sm">{b.dat_select || '\u2014'}</td>
                                        <td className="text-center relative">
                                            <button className={`btn btn-xs ${hasAccess ? 'btn-accent' : 'btn-disabled'}`}
                                                disabled={!hasAccess}
                                                onClick={() => hasAccess && setSelectedBuyer({ buyerCodes: [b.code], buyerName: b.buyer_name || '' })}>
                                                {hasAccess ? 'Начать' : 'Нет доступа'}
                                            </button>
                                            {hoveredRow === b.code && tooltip && (
                                                <div className="absolute right-0 top-full mt-1 bg-base-100 border border-base-300 rounded shadow-xl z-50 p-3 text-left whitespace-pre-line text-xs min-w-[220px]">
                                                    <div className="font-semibold mb-1 text-base-content/70">Доступ к отборке:</div>
                                                    {tooltip}
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
});

export default SelectionPage;
